<template>
    <v-container fluid class="pa-0">
        <v-card>
            <v-card-title primary-title>
                <v-row>
                    <v-col>Experiment Groups</v-col>
                    <v-spacer />
                    <v-col class="text-right" v-if="userCanManageExperimentGroups">
                        <v-btn color="primary" @click="showCreateExperimentGroupForm"
                            >Create Experiment Group</v-btn
                        >
                    </v-col>
                </v-row>
            </v-card-title>
            <v-card-text>
                <experiment-groups-list
                    :experiment-groups="experimentGroupsWithPropsForFiltering"
                    :search="props.search"
                    @delete-experiment-group="handleDeleteExperimentGroup"
                />
            </v-card-text>

            <v-dialog v-model="createExperimentGroupDialogVisible" max-width="720px">
                <experiment-group-form
                    :experiment-group="editExperimentGroup"
                    @cancelForm="closeCreateExperimentGroupForm"
                    @experimentGroupSaved="savedExperimentGroup"
                />
            </v-dialog>
        </v-card>
    </v-container>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { withLoadingScreen } from "@/lib/loading.js";
import {
    ExperimentGroupDTO,
    experimentGroupsAPI,
    SavedExperimentGroup,
} from "@/services/experimentGroups/experimentGroupsAPI";
import { useAuth } from "@/lib/ts/auth";
import ExperimentGroupsList from "@/components/ExperimentGroups/ExperimentGroupsList.vue";
import ExperimentGroupForm from "@/components/ExperimentGroups/ExperimentGroupForm.vue";

const auth = useAuth();
const userCanManageExperimentGroups = computed(() => {
    return auth.isAuthorized("ExperimentGroups:Manage");
});

interface Props {
    search: string | undefined;
}

const props = withDefaults(defineProps<Props>(), {
    search: "",
});

const experimentGroups = ref<ExperimentGroupDTO[]>([]);
const experimentGroupsWithPropsForFiltering = computed(() => {
   return experimentGroups.value.map((experimentGroup) => {
       return {
           ...experimentGroup,
           schemaConfigName: experimentGroup.schemaConfig?.name,
           experimentMethodName: experimentGroup.experimentMethod?.name,
       }
    })
});
const createExperimentGroupDialogVisible = ref(false);
const editExperimentGroup = ref<ExperimentGroupDTO>();

onMounted(async () => {
    await reloadExperimentGroups();
});
async function reloadExperimentGroups() {
    await withLoadingScreen(async () => {
        experimentGroups.value = await experimentGroupsAPI.fetchExperimentGroupsWithMetadata();
    });
}

function showCreateExperimentGroupForm() {
    editExperimentGroup.value = undefined;
    createExperimentGroupDialogVisible.value = true;
}

function closeCreateExperimentGroupForm() {
    editExperimentGroup.value = undefined;
    createExperimentGroupDialogVisible.value = false;
}

function savedExperimentGroup() {
    closeCreateExperimentGroupForm();
    reloadExperimentGroups();
}

async function handleDeleteExperimentGroup(experimentGroup: SavedExperimentGroup) {
    if (confirm(`Are you sure you want to delete group ${experimentGroup.name}?`)) {
        await withLoadingScreen(async () => {
            await experimentGroupsAPI.deleteExperimentGroup(experimentGroup.id);
            await reloadExperimentGroups();
        });
    }
}
</script>
